import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import './about.css';

const AboutPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    "https://images.unsplash.com/photo-1545870490-5d5c3bef8f5c?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    "https://images.unsplash.com/photo-1567801244563-9e7a273b21e9?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D",
    "https://images.unsplash.com/photo-1572086730895-8ad6d57b73e9?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  const slideVariants = {
    enter: { opacity: 0 },
    center: { opacity: 1 },
    exit: { opacity: 0 }
  };

  return (
    <>
      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-bg-container">
          {slides.map((slide, index) => (
            <motion.div
              key={index}
              className={`about-hero-bg ${currentSlide === index ? 'active' : ''}`}
              style={{ backgroundImage: `url(${slide})` }}
              variants={slideVariants}
              initial="enter"
              animate={currentSlide === index ? "center" : "exit"}
              transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="about-hero-overlay" />
            </motion.div>
          ))}
        </div>

        <div className="about-hero-content">
          <motion.div
            className="about-hero-content-inner"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div variants={itemVariants}>
              <div className="about-hero-title-container">
                <motion.span 
                  className="about-hero-title-line"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.4, duration: 0.8 }}
                />
                <motion.h1 
                  className="about-hero-title"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  About Parksy
                </motion.h1>
                <motion.span 
                  className="about-hero-title-line right"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.6, duration: 0.8 }}
                />
              </div>
            </motion.div>

            <motion.div variants={itemVariants}>
              <motion.p 
                className="about-hero-subtitle"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                Revolutionizing airport parking across the UK with smart technology and premium services
              </motion.p>
            </motion.div>

            <motion.div 
              className="about-hero-scroll-container"
              variants={itemVariants}
            >
              <motion.div
                className="about-hero-scroll"
                animate={{
                  y: [0, -5, 0],
                }}
                transition={{
                  repeat: Infinity,
                  duration: 2,
                  ease: "easeInOut"
                }}
              >
                <svg className="about-hero-scroll-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
                <span className="about-hero-scroll-text">Discover More</span>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>

        <div className="slide-indicators">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`slide-indicator ${currentSlide === index ? 'active' : ''}`}
              onClick={() => setCurrentSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Company Background Section */}
      <section className="company-story-section">
        <div className="section-container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ 
              duration: 0.8,
              ease: [0.16, 1, 0.3, 1]
            }}
          >
            <motion.h2
              initial={{ letterSpacing: 5, opacity: 0 }}
              whileInView={{ letterSpacing: 1, opacity: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="section-title"
            >
              Our Journey
            </motion.h2>
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="section-subtitle"
            >
              How Parksy transformed airport parking across the UK
            </motion.p>
          </motion.div>

          <motion.div 
            className="story-content"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ 
              duration: 1.2, 
              delay: 0.6,
              staggerChildren: 0.2
            }}
          >
            <div className="story-text">
              <motion.p
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                Parksy was founded to solve the stressful experience of airport parking. We started with a simple mission: to make airport parking as seamless as the flight itself. Our journey began at Heathrow in 2018 and we've since expanded to all major UK airports.
              </motion.p>
              <motion.p
                initial={{ y: 30, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
                Today, we're the UK's leading airport parking service, offering premium meet-and-greet, secure parking facilities, and valet services at all major airports. Our team of parking experts ensures your vehicle is safe while you travel.
              </motion.p>
            </div>
            
            <motion.div 
              className="story-stats"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ 
                duration: 1,
                staggerChildren: 0.2
              }}
            >
              {[
                { number: "12", label: "UK Airports" },
                { number: "500K+", label: "Cars Parked Yearly" },
                { number: "99.7%", label: "Customer Satisfaction" }
              ].map((stat, index) => (
                <motion.div 
                  key={index}
                  className="stat-item"
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  transition={{ 
                    type: "spring",
                    stiffness: 100,
                    damping: 10,
                    delay: 0.1 * index
                  }}
                  whileHover={{ 
                    y: -5,
                    transition: { duration: 0.3 }
                  }}
                >
                  <span className="stat-number">{stat.number}</span>
                  <span className="stat-label">{stat.label}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section">
        <div className="mission-background-pattern"></div>
        <div className="section-container">
          <motion.div 
            className="mission-content"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2 }}
          >
            <motion.div 
              className="mission-statement"
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <motion.h2
                initial={{ letterSpacing: 10, opacity: 0 }}
                whileInView={{ letterSpacing: 1, opacity: 1 }}
                transition={{ duration: 1, delay: 0.2 }}
              >
                Our Mission
              </motion.h2>
              <motion.blockquote
                initial={{ scale: 0.95, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                "To eliminate the stress of airport parking with premium, secure, and convenient solutions that let travelers focus on their journey."
              </motion.blockquote>
            </motion.div>

            <motion.div 
              className="mission-features"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ staggerChildren: 0.15 }}
            >
              {[
                {
                  icon: "✈️",
                  title: "Airport Proximity",
                  description: "Strategically located parking at all major UK airports"
                },
                {
                  icon: "🛡️",
                  title: "Maximum Security",
                  description: "24/7 monitored facilities with CCTV and patrols"
                },
                {
                  icon: "👨‍✈️",
                  title: "Meet & Greet",
                  description: "Professional valet service at terminal arrivals"
                },
                {
                  icon: "🔋",
                  title: "EV Charging",
                  description: "Electric vehicle charging while you travel"
                },
                {
                  icon: "🧳",
                  title: "Luggage Assistance",
                  description: "Help with luggage to make your transition smoother"
                },
                {
                  icon: "📱",
                  title: "Seamless Booking",
                  description: "Easy online reservation with instant confirmation"
                }
              ].map((feature, index) => (
                <motion.div 
                  key={index}
                  className="feature-card"
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  whileHover={{ 
                    y: -10,
                    boxShadow: "0 20px 40px rgba(2, 136, 209, 0.15)"
                  }}
                  transition={{ 
                    type: "spring",
                    stiffness: 100,
                    damping: 10,
                    delay: index * 0.1
                  }}
                >
                  <motion.div 
                    className="feature-icon"
                    whileHover={{ scale: 1.2 }}
                  >
                    {feature.icon}
                  </motion.div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                  <div className="feature-hover-indicator"></div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="core-values-section">
        <div className="section-container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <motion.h2
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Our Principles
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              The values that guide our airport parking excellence
            </motion.p>
          </motion.div>

          <motion.div 
            className="values-grid"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ staggerChildren: 0.1 }}
          >
            {[
              {
                icon: "🛡️",
                title: "Security First",
                description: "Your vehicle's safety is our highest priority with maximum security measures."
              },
              {
                icon: "⏱️",
                title: "Punctuality",
                description: "We understand flight schedules are non-negotiable and time-sensitive."
              },
              {
                icon: "👑",
                title: "Premium Service",
                description: "Luxury treatment for every customer, regardless of parking option."
              },
              {
                icon: "🌱",
                title: "Sustainability",
                description: "Eco-friendly initiatives including EV charging and carbon offset programs."
              },
              {
                icon: "🔧",
                title: "Innovation",
                description: "Continually improving our services with the latest technology."
              },
              {
                icon: "🤝",
                title: "Reliability",
                description: "Consistently delivering on our promises for stress-free travel."
              }
            ].map((value, index) => (
              <motion.div 
                key={index}
                className="value-card"
                initial={{ y: 60, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                whileHover={{ 
                  y: -10,
                  boxShadow: "0 20px 40px rgba(2, 136, 209, 0.2)"
                }}
                transition={{ 
                  type: "spring",
                  stiffness: 100,
                  damping: 10,
                  delay: index * 0.1
                }}
              >
                <motion.div 
                  className="value-icon"
                  whileHover={{ rotateY: 180 }}
                >
                  {value.icon}
                </motion.div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
                <div className="value-card-glow"></div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Technology Section */}
      <section className="technology-section">
        <div className="section-container">
          <motion.div 
            className="tech-content"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <div className="tech-text">
              <h2>Airport Parking Technology</h2>
              <p>
                SkyPark leverages advanced technology specifically designed for airport parking challenges. Our systems integrate with airport databases to provide real-time availability and optimize the parking experience.
              </p>
              <ul className="tech-features">
                <li>Flight tracking integration</li>
                <li>Automated check-in systems</li>
                <li>License plate recognition</li>
                <li>Real-time space availability</li>
              </ul>
            </div>
            <div className="tech-visual">
              <div className="tech-circle">
                <div className="circle-item">Flight Sync</div>
                <div className="circle-item">Secure Facilities</div>
                <div className="circle-item">Valet Management</div>
                <div className="circle-item main">SkyPark Tech</div>
                <div className="circle-item">Mobile Check-in</div>
                <div className="circle-item">Vehicle Tracking</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Airport Partners Section */}
      <section className="partners-section">
        <div className="section-container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2>Our Airport Partners</h2>
            <p>We serve all major UK airports with premium parking solutions</p>
          </motion.div>
          <motion.div 
            className="partners-grid"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ staggerChildren: 0.1 }}
          >
            {[
              "Heathrow", "Gatwick", "Manchester", "Stansted", 
              "Luton", "Edinburgh", "Birmingham", "Glasgow"
            ].map((airport, index) => (
              <motion.div 
                key={index}
                className="partner-card"
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="airport-icon">✈️</div>
                <h3>{airport}</h3>
                <p>Airport</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="section-container">
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2>Ready for stress-free airport parking?</h2>
            <p>Join thousands of travelers who trust us with their vehicles at UK airports</p>
            <div className="cta-buttons">
              <button className="cta-button primary">Book Airport Parking</button>
              <button className="cta-button secondary">View Airport Options</button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;